import AngleRightIcon from 'src/app/assets/icons/ico-angle-right.svg'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'

type Props = Readonly<{
  /** Whether this is the host panel's open section. */
  open: boolean
  /** The header tap; the host's `activeSectionId` decides what it does. */
  onToggle: () => void
  /** Exactly the booleans the section's markup reads. */
  flags: Readonly<{
    isCrashGame: boolean
    isIrlGame: boolean
    isIrlAltGame: boolean
    isSocialBettingGame: boolean
    isSnowRunGame: boolean
    isDuckRiverGame: boolean
    isCoinFlipGame: boolean
    isFishtankGame: boolean
  }>
}>

/**
 * The rules panel's error-handling section — the shared panel's own markup,
 * moved here (5.10c) so that `GameRulesSidebar` and Birdie's rules body mount
 * ONE section instead of two copies. Chrome and state as in
 * `PlacingABetSection`: the host keeps `activeSectionId`, this takes
 * `open` / `onToggle`.
 *
 * Bodies per game: crash (its own disconnection, lag and cancellation
 * boxes), the CCTV and clip games (a lag box, the cancellation grounds),
 * coin flip and fishtank (their own cancellation sentence), and the generic
 * pair every other game gets. Birdie takes the generic pair unchanged —
 * every flag false — so it needs no flag of its own here, and the markup
 * reads nothing from the game.
 *
 * The section owns its translator on the panel's namespace, the language
 * read into a name first, for the reasons given in `PlacingABetSection`.
 */
export function ErrorHandlingSection({ open, onToggle, flags }: Props) {
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage, 'gameRules')
  const {
    isCrashGame,
    isIrlGame,
    isIrlAltGame,
    isSocialBettingGame,
    isSnowRunGame,
    isDuckRiverGame,
    isCoinFlipGame,
    isFishtankGame
  } = flags
  return (
    <div className={`section section-collapsible ${open ? 'open' : ''}`}>
      <div className="section-header" onClick={onToggle}>
        <h3>{t('Error Handling')}</h3>
        <div className="section-header-icon">
          <AngleRightIcon />
        </div>
      </div>
      <div className="section-content">
        {isCrashGame ? (
          <div className="info-box margin-bottom">
            <h3>{t('Disconnection Policy')}</h3>
            <p>
              {t(
                'If you disconnect mid-round, any stake you already cashed out is safe and settled. A stake still riding will settle on its result — collected by your auto cash-out if you set one, otherwise lost if the round crashed. Check Bet History when you reconnect.'
              )}
            </p>
          </div>
        ) : (
          <div className="info-box margin-bottom">
            <h3>{t('Disconnection Policy')}</h3>
            <p>
              {t(
                'If you disconnect during a race, any placed bets remain valid and will be settled normally. Upon reconnecting, you can view all bet outcomes in the Bet History panel.'
              )}
            </p>
          </div>
        )}

        {isCrashGame && (
          <div className="info-box margin-bottom">
            <h3>{t('Stream Quality & Lag')}</h3>
            <p>
              {t(
                'If the video briefly lags on your end, it never changes the result. The crash point is fixed in advance and your cash-outs are timed on our server, not on your video — what you see is a faithful replay of an outcome that’s already decided.'
              )}
            </p>
          </div>
        )}

        {(isIrlAltGame || isIrlGame) && (
          <div className="info-box margin-bottom">
            <h3>{t('Stream Quality & Lag')}</h3>
            <p>
              {isSnowRunGame
                ? t(
                    'If playback briefly lags on your end, it does not affect the result — the AI analyses the exact same footage you see, and all bets settle identically.'
                  )
                : isDuckRiverGame
                  ? t(
                      'The duck river stream may occasionally experience brief lag or lower quality. This is normal and does not affect fairness — the AI analyses the exact same segment you see, so all bets are settled on identical footage regardless of stream conditions.'
                    )
                  : isSocialBettingGame
                    ? t(
                        "Uploaded clips may vary in resolution or quality depending on the creator's camera. This is normal and does not affect fairness — the AI analyses the exact same clip you see, so all bets are settled on identical footage."
                      )
                    : t(
                        'Live CCTV streams may occasionally experience brief lag, frame drops, or lower quality due to weather, network conditions, or camera limitations. This is normal and does not affect fairness — the AI analyses the exact same segment you see, so all bets are settled on identical footage regardless of stream conditions.'
                      )}
            </p>
          </div>
        )}

        {isCrashGame ? (
          <div className="info-box margin-bottom">
            <h3>{t('Round Cancellations')}</h3>
            <p>
              {t(
                'A round is cancelled and every stake refunded if the stream fails completely, the crash point can’t be published, or our system is too slow during a phase change. Refunds are automatic.'
              )}
            </p>
          </div>
        ) : (
          <div className="info-box margin-bottom">
            <h3>{t('Race & Technical Issues')}</h3>
            {isIrlAltGame || isIrlGame ? (
              <>
                <p>
                  <strong>{t('When is a round canceled?')}</strong>
                </p>
                <ul>
                  <li>{t('Complete stream failure — no footage is captured for the round')}</li>
                  <li>{t('Confirmed AI malfunction — no count can be produced')}</li>
                  <li>
                    {t(
                      'System processing delay — if our system is slow to respond during the transition from betting to counting, or during result settlement, the round is canceled as a safety measure so no bet is left unresolved'
                    )}
                  </li>
                </ul>
                <p>{t('All bets from canceled rounds are automatically refunded.')}</p>
                <p>
                  <strong>{t('What is NOT grounds for cancellation?')}</strong>
                </p>
                <p>
                  {t(
                    'Visual lag, brief buffering, or lower stream quality. The AI and all players see the same segment, ensuring a fair outcome every round.'
                  )}
                </p>
              </>
            ) : (
              <p>
                {isCoinFlipGame
                  ? t(
                      'If a flip encounters any error or technical issue (such as the coin getting stuck, machine malfunction, or detection problems), the round will be canceled and all plays refunded. This ensures all flip outcomes can be properly verified and shown to players.'
                    )
                  : isFishtankGame
                    ? t(
                        'Rounds are canceled and bets refunded if technical issues occur, including camera failures, machine malfunctions, or any situation where the result cannot be 100% verified.'
                      )
                    : t(
                        'If a race encounters any error or technical issue (such as camera malfunction, sequence interruption, or visibility problems at the finish line), the race will be paused and players notified immediately. If the issue can be resolved without affecting the race integrity, it will continue normally. Otherwise, the race will be canceled and all bets refunded, typically within a few minutes. This ensures all race outcomes can be properly verified and shown to players.'
                      )}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
